import { Dispatch, FC, MouseEvent, SetStateAction, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import isString from 'lodash/isString'
import moment from 'moment'
import { v4 as uuid } from 'uuid'
import more from '@assets/images/more.svg'
import { FormInput } from '@microservices/wiskey-react-components'
import { Box, Grid, IconButton, InputAdornment, SxProps, Typography, useTheme } from '@mui/material'

import { RowStylesWrapper } from '@pages/ConfiguredEntity/components/RowStylesWrapper'

import { ActionButton } from '@components/ActionButton'
import { CustomSvgIcon } from '@components/CustomSvgIcon/CustomSvgIcon'
import { FormContext } from '@components/DisplayForm'
import { getBooleanSelectInputProps } from '@components/DisplayForm/helpers'
import { checkValidElementInLine } from '@components/DisplayForm/helpers/checkValidElementInLine'
import { useDisplayFormState, useFormFields } from '@components/DisplayForm/hooks'
import { FormInputWrapper } from '@components/FormInputWrapper'
import { EnumAutocompleteController } from '@components/hookFormControllers/EnumAutocompleteController'
import { SyntheticAutoCompleteController } from '@components/hookFormControllers/SynthericAutoCompleteController/SyntheticAutoCompleteController'
import { MappedCheckboxForForm } from '@components/MappedCheckbox'
import { MaskedTimeInputForForm } from '@components/MaskedTimeInputForForm/MaskedTimeInputForForm'

import { useFetchObjectDataMutation } from '@redux/api'
import { openFormDialog, openStringEditDialog } from '@redux/reducers/dialogWindowManager.reducer'
import { addFormHistory, createHistoryByForm } from '@redux/reducers/formHistory.reducer'
import { showMessage } from '@redux/reducers/snackbar.reducer'

import { useAppDispatch, useAppSelector, useChangeVariableWithAction, useDebounce } from '@hooks'
import {
  generalFieldStylesByMode,
  getFormCodeFromExpandsParams,
  getFormCodeFromParams,
  getInitialPositionWindow,
  getPlaceholderByField,
  getSecondsFromTime,
  getTimeFromSeconds,
  isObjectValueType,
  transformParamsToExpands,
} from '@helpers'
import {
  BIND_TYPE,
  ENTITY,
  FIELD_VALUE_FORMAT,
  FIELD_VALUE_TYPE,
  FLEXIBLE,
  FORM_ELEMENT_TYPE,
  FORM_FIELD_LABEL_POSITION,
  FORM_TYPE,
  GENERATOR_INPUT_TYPE,
  LABEL_PLACEMENT_RECORD,
  LINE_GAP,
  MAX_INPUT_LENGTH,
  MIN_COLLAPSED_LC_HEIGHT,
  MIN_HEIGHT_FIELD,
  MIN_HEIGHT_FIELD_DIALOG,
  MIN_HEIGHT_VIEW,
  OBJECT_FIELD_TYPE,
  REGEX,
  UPDATES_ROW_CLASSNAME,
} from '@constants'
import {
  DIALOG_WINDOW_TYPE,
  FieldObjectParams,
  FormDataEnriched,
  FormElement,
  FormLine,
  FormRow,
  NewObjectDataLinkType,
  Row,
} from '@types'

import { EmbeddedListControl } from '../EmbeddedListControl'
import { JSField } from '../JSField'
import { ObjectPickerControllerVariableWrapper } from '../ObjectPickerControllerVariableWrapper'

export const LinesWithElements: FC<{
  lines: FormLine<FormDataEnriched>[]
  tabId: number
  listControlStates: Record<number, { expanded: boolean; height: number }>
  setListControlStates: Dispatch<
    SetStateAction<Record<number, { expanded: boolean; height: number }>>
  >
}> = ({ lines, tabId, listControlStates, setListControlStates }) => {
  const { t } = useTranslation()
  const {
    form,
    row,
    links,
    onSetNewForm,
    isFetchingForm,
    viewPath,
    onResetEdit,
    objectId,
    type: formType,
    isViewEdit,
    isDropdownWindowOpenFormState,
    handleSetDropdownWindowOpenFormState,
    initialCommonParameters,
    globalId,
    currentLink,
    dialogId,
    watch,
    setValueOnForm,
    isSaveByEnterDisabled,
    handleSetSaveByEnterDisabled,
    formCodeToFetchBy,
    isSectionWindow,
    defaultValues,
    latestVariables,
    outerVariables,
  } = useContext(FormContext)
  const {
    isViewType,
    isDirty,
    dirtyFields: dirtyFieldsObj,
    isFormDialogWindow,
  } = useDisplayFormState()
  const dispatch = useAppDispatch()
  const { objectsWithDirtyRecords } = useAppSelector(state => state.changedFormFields)
  const { dialogWindows } = useAppSelector(state => state.dialogWindowManager)

  const allFieldsWithCurrentValues = watch()

  useChangeVariableWithAction(form?.actions, outerVariables, watch, defaultValues)

  useEffect(() => {
    const initialStates: Record<number, { expanded: boolean; height: number }> = {}
    lines.forEach(line => {
      line.elements.forEach(el => {
        if (el.type === FORM_ELEMENT_TYPE.VIEW) {
          initialStates[el.id] = { expanded: true, height: MIN_HEIGHT_VIEW }
        }
      })
    })
    setListControlStates(prev => ({ ...prev, ...initialStates }))
  }, [lines])

  const flattenedAndFilteredFieldsWithCurrentValues = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(allFieldsWithCurrentValues)
          .map(([key, value]) => [
            key,
            value && typeof value === 'object' && 'id' in value ? value.id : value,
          ])
          .filter(([_, value]) => {
            return value !== ''
          })
      ),
    [JSON.stringify(allFieldsWithCurrentValues)]
  )

  const theme = useTheme()
  const debouncedFlattenedAndFilteredFieldsWithCurrentValues = useDebounce(
    flattenedAndFilteredFieldsWithCurrentValues,
    300
  )
  const isCreateOrEditMode =
    formType === FORM_TYPE.EDIT || isViewEdit || formType === FORM_TYPE.CREATE

  const [fetchExpandObjectRecord] = useFetchObjectDataMutation()

  const formFields = useFormFields({ formCode: formCodeToFetchBy })

  const handleNavigateOtherForm = (
    { objectId, objectCode, formCode, title, isShiftKey, typeId }: NewObjectDataLinkType,
    event?: MouseEvent<HTMLButtonElement>
  ) => {
    if (isDirty && !confirm(t('notifications.leave'))) {
      return
    }
    if (form && !isFetchingForm) {
      const lastLink = links?.at(-1) || currentLink
      if (lastLink) {
        if (isShiftKey) {
          const id = uuid()
          const initialPosition = event ? getInitialPositionWindow(event) : undefined
          const openedFormsCount = dialogWindows.filter(
            dialog =>
              [DIALOG_WINDOW_TYPE.FORM, DIALOG_WINDOW_TYPE.VIEW].includes(dialog.type) &&
              !dialog.hidden
          ).length
          const maxOpenedFormsCount = Number(initialCommonParameters.numberOfWindows)

          if (openedFormsCount === maxOpenedFormsCount) {
            dispatch(
              showMessage({
                type: 'info',
                text: t('error.dynamicFormsExceeded', { count: maxOpenedFormsCount }),
              })
            )

            return
          }

          dispatch(
            openFormDialog({
              id,
              parentDialogId: null,
              type: DIALOG_WINDOW_TYPE.FORM,
              title: null,
              meta: {
                formCode,
                objectId,
                globalId: id,
                // TODO Возможно стрельнет отсутствие viewCode, значение path, entityType и event
                viewCode: '',
                path: formCode,
                entityType: ENTITY.VIEW,
                event: FORM_TYPE.VIEW,
                parentFormId: globalId,
                typeId,
              },
              initialPosition,
            })
          )

          dispatch(
            createHistoryByForm({
              id,
              parentForm: currentLink,
              childForm: {
                formCode,
                objectCode,
                objectId,
                event: currentLink.event,
                title,
                id,
                isWindow: true,
                typeId,
              },
            })
          )

          return
        }
        const id = uuid()

        // если форма была открыта по ссылке
        if (!links) {
          dispatch(
            createHistoryByForm({
              id: globalId,
              parentForm: currentLink,
              childForm: {
                formCode,
                objectCode,
                objectId,
                event: currentLink.event,
                title,
                id,
                isWindow: false,
                typeId,
              },
            })
          )

          onResetEdit()

          return
        }

        dispatch(
          addFormHistory({
            id: globalId,
            form: {
              formCode,
              objectCode,
              objectId,
              event: currentLink.event,
              title,
              id,
              isWindow: true,
              typeId,
            },
          })
        )

        onResetEdit()
      }
    }
  }

  const renderJSValueField = (value: string, row: Row) => {
    const renderValue = new Function('srcObj', 'moment', value)
    try {
      const result = renderValue(row, moment)

      return `${result}`.trim()
    } catch {
      return undefined
    }
  }

  const getInputTypeByValueType = (field: FormRow): GENERATOR_INPUT_TYPE => {
    if (field.isAddFieldValues) {
      return GENERATOR_INPUT_TYPE.AUTOCOMPLETE
    }

    switch (field.valueType) {
      case FIELD_VALUE_TYPE.DATETIME:
        return GENERATOR_INPUT_TYPE.DATE_TIME_PICKER
      case FIELD_VALUE_TYPE.BOOLEAN:
        return GENERATOR_INPUT_TYPE.SELECT
      case FIELD_VALUE_TYPE.BINARY:
        return GENERATOR_INPUT_TYPE.FILE_PICKER
      default:
        return GENERATOR_INPUT_TYPE.INPUT
    }
  }

  const getTypeByValueType = (valueType: FIELD_VALUE_TYPE | null) => {
    if (valueType === FIELD_VALUE_TYPE.DOUBLE || valueType === FIELD_VALUE_TYPE.INTEGER) {
      return 'number'
    }

    return
  }

  const onExpandedChange = (formElementId: number, height: number) => {
    setListControlStates(prev => ({
      ...prev,
      [formElementId]: {
        height: Math.max(height, MIN_COLLAPSED_LC_HEIGHT),
        expanded: height > MIN_COLLAPSED_LC_HEIGHT,
      },
    }))
  }

  const handleGetFormCodeParams = ({
    field,
    objectId,
    fieldParams,
    typeId,
  }: {
    fieldParams: FieldObjectParams
    objectId: string
    field: FormRow
    typeId: FIELD_VALUE_TYPE
  }) => {
    const transformParams = transformParamsToExpands(fieldParams)
    const paramsForExpandsRequest = transformParams.map(item => item.expandRequest)

    fetchExpandObjectRecord({
      objectCode: field.embeddedObjectCode,
      objectId,
      expands: paramsForExpandsRequest,
    })
      .unwrap()
      .then(res => {
        if (field.embeddedObjectCode) {
          const findFormCodeByExpandedFieldValue = getFormCodeFromExpandsParams(
            transformParams,
            res
          )
          const findFormCodeByFieldValue = getFormCodeFromParams(fieldParams, res)
          const defaultFormCode = isString(fieldParams.formCode) && fieldParams.formCode

          if (findFormCodeByFieldValue) {
            handleNavigateOtherForm({
              formCode: findFormCodeByExpandedFieldValue || findFormCodeByFieldValue,
              objectId,
              objectCode: field.embeddedObjectCode,
              title: findFormCodeByFieldValue,
              isShiftKey: false,
              typeId,
            })

            return
          }
          if (defaultFormCode) {
            handleNavigateOtherForm({
              formCode: defaultFormCode,
              objectId,
              objectCode: field.embeddedObjectCode,
              title: defaultFormCode,
              isShiftKey: false,
              typeId,
            })
          }
        }
      })
  }

  const renderInput = ({
    data,
    titleHidden,
    labelPosition = FORM_FIELD_LABEL_POSITION.TOP,
  }: FormElement<FormDataEnriched>) => {
    const field = data as FormRow

    const objectWithFields = objectsWithDirtyRecords[currentLink.objectCode]
    // TODO Хардкод _id
    const dirtyFields = (objectWithFields && objectWithFields[row?._id]) || []

    const className = dirtyFields.includes(field.code) ? UPDATES_ROW_CLASSNAME : undefined
    const userRequired = field.userRequired ?? false
    const isFieldRequired = userRequired || field.defaultRequired

    const fieldLabel = titleHidden ? undefined : field.title
    const fieldlabelPlacement = LABEL_PLACEMENT_RECORD[labelPosition]

    const readOnly = isViewType || !field.editField
    const disabled = readOnly
    const placeholder = getPlaceholderByField(field)

    const watchCurrentField = watch(field.code)

    const labelWidthSx: SxProps = {
      width: undefined,
      minWidth: undefined,
      flexBasis: 120,
      ...(fieldlabelPlacement === 'top' && {
        width: '100%',
        flexBasis: undefined,
      }),
    }

    const handleValidateValue = (value: string) => {
      // return REGEX.OBJECT_PICKER_FORBIDDEN_CHARS.test(value)
      return true
    }

    const handleSetOuterValueForMappedCheckbox = (value: number | boolean) => {
      setValueOnForm(field.code, value as unknown as never, {
        shouldDirty: true,
      })
    }

    const handleSetOuterValueForTime = (value: string) => {
      setValueOnForm(field.code, getSecondsFromTime(value) as unknown as never, {
        shouldDirty: true,
      })
    }

    const handleSetOuterValueStringEdit = (value: string) => {
      setValueOnForm(field.code, value as unknown as never, {
        shouldDirty: true,
        shouldValidate: true,
      })
    }

    const handleOpenWindowStringEdit = () => {
      // для открытия формы по нажатию на иконку "стрелка вправо" или по двойному клику на строку во вью смотрим на isViewEdit,
      // через открытие формы на редактирование на иконку "карандаш" смотрим на formType === FORM_TYPE.EDIT
      // также при создании формы также можно редактировать строковое поле в отдельном окне
      dispatch(
        openStringEditDialog({
          id: uuid(),
          parentDialogId: dialogId,
          type: DIALOG_WINDOW_TYPE.STRING_EDIT,
          title: t('stringEdit.title', { field: field.title }),
          meta: {
            text: watchCurrentField ?? row[field.code],
            setOuterValue: handleSetOuterValueStringEdit, // TODO исправить на ивенты, тк много ошибок в консоли
            readonly: !isCreateOrEditMode || !field?.editField,
          },
        })
      )
    }

    const asCheckbox =
      field.valueFormat === FIELD_VALUE_FORMAT.NUMBER ||
      field.valueFormat === FIELD_VALUE_FORMAT.BOOLEAN

    if (field.isAddFieldValues) {
      return (
        <SyntheticAutoCompleteController
          className={className}
          dirtyFields={dirtyFieldsObj}
          disabled={disabled}
          isDialogWindow={isFormDialogWindow}
          label={fieldLabel}
          labelPlacement={fieldlabelPlacement}
          name={field.code}
          objectCode={field.objectCode ?? ''}
          options={field.fieldValues?.map(({ value, label }) => ({ id: value, label }))}
          placeholder={placeholder}
          readOnly={readOnly}
          rules={{ required: isFieldRequired }}
          valueType={field.valueType as FIELD_VALUE_TYPE}
          labelSx={{
            color: theme.palette.color.formCardLabel,
            fontWeight: 400,
            letterSpacing: 0.5,
            ...labelWidthSx,
          }}
        />
      )
    }

    if (asCheckbox && field.valueFormat) {
      return (
        <MappedCheckboxForForm
          formRelatedProps={{
            dirtyFields: dirtyFieldsObj,
            field: field.code,
            name: field.code,
            updateField: Boolean(className),
            isDialogWindow: isFormDialogWindow,
            label: fieldLabel,
            labelPosition: fieldlabelPlacement,
          }}
          mappedCheckboxProps={{
            valueFormat: field.valueFormat,
            disabled,
            outerValue: watchCurrentField as unknown as boolean | number,
            setOuterValue: handleSetOuterValueForMappedCheckbox,
            checkboxSx: {
              top: 0,
              background: theme => (isViewEdit ? theme.palette.background.default : 'inherit'),
            },
          }}
        />
      )
    }

    if (field.valueFormat === FIELD_VALUE_FORMAT.TIME) {
      return (
        <MaskedTimeInputForForm
          formRelatedProps={{
            dirtyFields: dirtyFieldsObj,
            field: field.code,
            name: field.code,
            updateField: Boolean(className),
            isDialogWindow: isFormDialogWindow,
            label: fieldLabel,
            labelPosition: fieldlabelPlacement,
            labelWidthSx,
          }}
          inputMaskProps={{
            outerValue: getTimeFromSeconds(Number(watchCurrentField)),
            setOuterValue: handleSetOuterValueForTime,
            disabled,
            placeholder,
          }}
        />
      )
    }

    if (isObjectValueType(field.valueType)) {
      return (
        <ObjectPickerControllerVariableWrapper
          key={field.code}
          allFieldsWithCurrentValues={debouncedFlattenedAndFilteredFieldsWithCurrentValues}
          className={className}
          dirtyFields={dirtyFieldsObj}
          disableClearable={isCreateOrEditMode && !disabled ? false : true}
          disabled={disabled}
          dropDownList={field.dropDownList}
          dropDownWindow={field.dropDownWindow}
          fieldId={Number(field.id)}
          formId={Number(form?.id)}
          formObjectCode={form?.objectCode}
          formObjectId={objectId}
          formType={formType}
          formatDate={field.formatDate || undefined}
          generalFilterField={field?.generalFilterField}
          getValue={handleNavigateOtherForm}
          handleSetDropdownWindowOpenFormState={handleSetDropdownWindowOpenFormState}
          isDialogWindow={isFormDialogWindow}
          isDropDownList={Boolean(field.dropDownList)}
          isDropDownWindow={Boolean(field.dropDownWindow)}
          isDropdownWindowOpenFormState={isDropdownWindowOpenFormState}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          isViewEdit={isViewEdit}
          label={fieldLabel}
          labelPlacement={fieldlabelPlacement}
          multiple={field.type === OBJECT_FIELD_TYPE.ARRAY}
          name={field.code}
          objectCode={field.embeddedObjectCode}
          objectFormCode={field.objectFormCode}
          objectFormCodeparams={field.params}
          objectFormTitle={field.objectFormTitle}
          objectValue={field.objectValue}
          parentDialogId={dialogId}
          pathArray={field.pathArray}
          placeholder={placeholder}
          readOnly={readOnly}
          rules={{ required: isFieldRequired }}
          tabId={tabId}
          type={field.type}
          valueType={field.valueType}
          getFormCodeParams={(fieldParams, objectId, typeId) =>
            handleGetFormCodeParams({ fieldParams, objectId, typeId, field })
          }
          labelSx={{
            color: theme.palette.color.formCardLabel,
            fontWeight: 400,
            letterSpacing: 0.5,
            ...labelWidthSx,
          }}
          onValidateInputValue={handleValidateValue}
        />
      )
    }

    if (field.type === OBJECT_FIELD_TYPE.ENUM) {
      return (
        <EnumAutocompleteController
          className={className}
          defaultValues={defaultValues}
          dirtyFields={dirtyFieldsObj}
          disabled={disabled}
          fieldCode={field.value}
          isDialogWindow={isFormDialogWindow}
          label={fieldLabel}
          labelPlacement={fieldlabelPlacement}
          name={field.code}
          objectCode={field.objectCode ?? ''}
          placeholder={placeholder}
          readOnly={readOnly}
          rules={{ required: isFieldRequired }}
          valueType={field.valueType as FIELD_VALUE_TYPE}
          labelSx={{
            color: theme.palette.color.formCardLabel,
            fontWeight: 400,
            letterSpacing: 0.5,
            ...labelWidthSx,
          }}
        />
      )
    }

    if (field.bindType === BIND_TYPE.JS) {
      return (
        <JSField
          key={field.code}
          className={className}
          dirtyFields={dirtyFieldsObj}
          isDialogWindow={isFormDialogWindow}
          label={fieldLabel}
          labelPlacement={fieldlabelPlacement}
          name={field.code}
          placeholder={placeholder}
          value={renderJSValueField(field.value, row)}
          labelSx={{
            color: theme.palette.color.formCardLabel,
            fontWeight: 400,
            letterSpacing: 0.5,
            ...labelWidthSx,
          }}
        />
      )
    }

    return (
      <FormInputWrapper
        enterDisabled={isSaveByEnterDisabled}
        handleSetEnterDisabled={handleSetSaveByEnterDisabled}
        valueType={field.valueType}
      >
        <FormInput
          {...(field.valueType === FIELD_VALUE_TYPE.BOOLEAN
            ? getBooleanSelectInputProps({
                dirtyFieldsObj,
                booleanSelectValue: watchCurrentField,
                field,
                className,
                isFormDialogWindow,
                handleSetOuterValueForMappedCheckbox,
              })
            : {})}
          key={field.code}
          applyDateByWholeNumber={true}
          dateTimePickerViews={field.isEnabledTimeMenu ? undefined : ['year', 'month', 'day']}
          disabled={disabled}
          formatDate={field.formatDate || undefined}
          inputType={getInputTypeByValueType(field)}
          isGetNullWhenBlur={false}
          label={fieldLabel}
          labelPlacement={fieldlabelPlacement}
          multiline={field.isMultiline}
          {...(field.isMultiline && { minRows: 3 })}
          maxLengthInput={MAX_INPUT_LENGTH}
          name={field.code}
          placeholder={placeholder}
          placeholderDatePicker={placeholder}
          popoverDateShiftMode={true}
          readOnly={readOnly}
          rules={{ required: isFieldRequired }}
          showEstimatedDate={true}
          type={getTypeByValueType(field.valueType)}
          PopperProps={{
            sx: {
              zIndex: theme.zIndex.tooltip,
            },
          }}
          errorIconSxForDate={{
            top: '1px',
            right: '28px',
          }}
          formLabelSx={{
            '*': {
              transition: '0.2s ease-in',
            },
            '& > .MuiFormControl-root': {
              ...generalFieldStylesByMode(
                theme,
                dirtyFieldsObj,
                field.code,
                Boolean(className),
                isFormDialogWindow
              ),
              '& .MuiInputBase-root .MuiInputAdornment-root .MuiButtonBase-root': {
                padding: isFormDialogWindow ? 0 : 1,
              },
            },
          }}
          labelSx={{
            ...labelWidthSx,
            '*': {
              transition: '0.2s ease-in',
            },
            fontWeight: 400,
            letterSpacing: 0.5,
            color: theme.palette.color.default,
          }}
          replacePattern={
            field.valueType === FIELD_VALUE_TYPE.INTEGER ? REGEX.REPLACE_ONLY_NUMBER : undefined
          }
          textFieldInputProps={{
            autoComplete: 'off',
            ...(field.valueType === FIELD_VALUE_TYPE.STRING && {
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton size={'small'} onClick={() => handleOpenWindowStringEdit()}>
                    <CustomSvgIcon src={more} sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </InputAdornment>
              ),
            }),
          }}
        />
      </FormInputWrapper>
    )
  }

  const generateElement = (element: FormElement<FormDataEnriched>) => {
    const { data } = element
    const isListControl = data instanceof Object && 'objectCode' in data
    switch (element.type) {
      case FORM_ELEMENT_TYPE.FIELD:
        return renderInput(element)
      case FORM_ELEMENT_TYPE.SPACE:
        return <></>
      case FORM_ELEMENT_TYPE.TEXT:
        return (
          <Typography
            title={element.title}
            sx={{
              overflowX: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: element.textAlign ? element.textAlign.toLowerCase() : 'left',
            }}
          >
            {element.title}
          </Typography>
        )
      case FORM_ELEMENT_TYPE.ACTION_BUTTON:
        return (
          <ActionButton
            currentRowValues={flattenedAndFilteredFieldsWithCurrentValues}
            element={element}
            fieldsFormConfig={form?.fields}
            latestVariables={latestVariables}
            outerVariables={outerVariables}
            row={row}
          />
        )
      case FORM_ELEMENT_TYPE.VIEW:
        if (isListControl) {
          const formElementId = element.id
          const state = listControlStates[formElementId] || { expanded: true, height: 250 }

          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: MIN_COLLAPSED_LC_HEIGHT,
                flex: state.expanded ? 1 : 'none',
                overflow: 'hidden',
                transition: 'height 0.2s ease-in-out',
              }}
            >
              <EmbeddedListControl
                entityCode={data.code}
                entityId={data.id}
                expandable={element.expandable}
                getLeaveForm={() => isDirty && !confirm(t('notifications.leave'))}
                isListControlDialogWindow={isFormDialogWindow}
                isNestedView={true}
                parentFormElementId={element.id}
                parentFormIsDirty={isDirty}
                parentFormIsSectionWindow={isSectionWindow}
                parentFormObjectCode={form?.objectCode}
                parentFormObjectId={currentLink.objectId || objectId}
                parentFormObjectIdType={currentLink.typeId}
                path={viewPath}
                title={data.title}
                titleHidden={element.titleHidden}
                type={ENTITY.LIST_CONTROL}
                windowHeight={state.expanded ? state.height : MIN_COLLAPSED_LC_HEIGHT}
                outerRequestData={{
                  redifinitions: element.formToLcConnections,
                  srcObj: row,
                }}
                outerData={{
                  variables: latestVariables,
                }}
                onExpandedChange={height => onExpandedChange(formElementId, height)}
                onResetEditForm={onResetEdit}
              />
            </Box>
          )
        }
    }
  }

  return (
    <Box sx={{ ...FLEXIBLE, gap: LINE_GAP }}>
      {lines.map(line => {
        const isListControl = line.elements.some(element => element.type === FORM_ELEMENT_TYPE.VIEW)
        const listControlId = line.elements?.[0]?.id
        const isListControlExpanded = listControlStates[listControlId]?.expanded

        return (
          <Grid
            key={line.id}
            container
            spacing={1}
            sx={{
              ...FLEXIBLE,
              flexWrap: 'nowrap',
              flexDirection: 'row',
              flex: isListControl ? 1 : undefined,
              minHeight: isListControl
                ? isListControlExpanded
                  ? MIN_HEIGHT_VIEW
                  : MIN_COLLAPSED_LC_HEIGHT
                : undefined,
              maxHeight: isListControl
                ? isListControlExpanded
                  ? 'auto'
                  : MIN_COLLAPSED_LC_HEIGHT
                : 'auto',
            }}
          >
            {line.elements.map(element => {
              const isValidElement = checkValidElementInLine(formFields, element)

              return isValidElement ? (
                <Grid
                  key={element.id}
                  item
                  xs={element.size || 12}
                  sx={{
                    minHeight: isFormDialogWindow ? MIN_HEIGHT_FIELD_DIALOG : MIN_HEIGHT_FIELD,
                    ...(element.type === FORM_ELEMENT_TYPE.VIEW ? FLEXIBLE : undefined),
                    ...(element.type === FORM_ELEMENT_TYPE.VIEW
                      ? isListControlExpanded
                        ? { flex: 1, height: 'auto', minHeight: MIN_HEIGHT_VIEW }
                        : { flex: 'none', maxHeight: MIN_COLLAPSED_LC_HEIGHT }
                      : undefined),
                    // pointerEvents: `${!element?.data?.editField}` ? 'none' : 'auto',
                  }}
                >
                  <RowStylesWrapper isDialogWindow={isFormDialogWindow}>
                    {generateElement(element)}
                  </RowStylesWrapper>
                </Grid>
              ) : null
            })}
          </Grid>
        )
      })}
    </Box>
  )
}
